import { REDDIT_SUPERGRID_CONFIG } from 'wb-reddit/app/routes/superGrid/config';
import {
	columnEditorPlugin,
	ColumnModeTypeEnum,
	nameSizingPlugin,
	refocusCell,
	SuperGridAnchors,
	useSuperGridPlugin,
	focusSuperGridCell,
	useSuperGridEngine,
	useSupergridHorizontalMutationData,
	useSupergridMutations,
	CreateAnchorBody,
	validatePlugin,
	elasticPropertyPlugin,
} from '@aleph/supergrid';
import React, {
	useCallback, useEffect, useMemo, useState,
} from 'react';
import {
	Button,
	ButtonAppearance,
	ButtonColor,
	DirectionRight,
	TrashCanIcon,
	processValueFromJSON,
	processValueToJSON,
	updateAndReturnAffectedChildren,
	Floaty, Tooltip,
	ButtonSize,
	useSupergridAI,
} from '@aleph/shared-components';
import {
	MAPPED_COLUMN_NAMES, RedditLevel, getClientRecursively, isPendingAdAccount,
} from 'wb-reddit/app/routes/superGrid/utils';
import { DEFAULT_REDDIT_COL_DEF } from 'wb-reddit/app/routes/superGrid/view/utils';
import { motion } from 'framer-motion';
import { getRedditOpenAiHeadlines } from 'wb-reddit/app/routes/superGrid/openAi/utils';
import {
	REDDIT_CROSS_LEVEL_DEPENDENCIES,
	RedditSuperGridMutationsConfig,
} from 'wb-reddit/app/routes/superGrid/mutator/utils';
import { preValidateRedditEntities } from 'wb-reddit/app/routes/superGrid/validation/utils';
import useMutationsUndoRedoPlugin from 'wb-reddit/app/routes/superGrid/hooks/useMutationsUndoRedoPlugin';
import {
	GridApi, GridReadyEvent, RowGroupOpenedEvent, CellValueChangedEvent, CellKeyDownEvent, BodyScrollEvent,
	FillOperationParams, FillEndEvent, IRowNode,
} from 'ag-grid-community';
import { LicenseManager } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import useInstantEditPlugin from 'wb-reddit/app/routes/superGrid/hooks/useInstantEditPlugin';
import {
	changeMutationWithAutoName,
	createSequence, handleCellLoading, isNamingFormula, transformNamingExpression,
} from 'wb-reddit/app/common/components/autoNaming/promptHelper';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ValidationNavigator } from 'wb-spotify/app/common/supergridValidation';

LicenseManager.setLicenseKey(
	// eslint-disable-next-line max-len
	'Using_this_AG_Grid_Enterprise_key_( AG-043155 )_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_( legal@ag-grid.com )___For_help_with_changing_this_key_please_contact_( info@ag-grid.com )___( Httpool d.o.o. )_is_granted_a_( Multiple Applications )_Developer_License_for_( 3 )_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_AG_Grid_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_( 1 )_Production_Environment___This_key_works_with_AG_Grid_Enterprise_versions_released_before_( 1 July 2024 )____[v2]_MTcxOTc4ODQwMDAwMA==3f4d73dbd0b55114fae1030bca4daf44',
);

const hiddenLevels = [RedditLevel.BREAKDOWN as const, 'GLOBAL' as const];

const visibleColumns = {
	GLOBAL: [
		{ key: 'index', force: true },
		{ match: 'Name', force: true },
	],
};

const RedditHorizontalMutatorGrid = ({ confirmMutations }) => {
	const horizontalRowData = useSupergridHorizontalMutationData(REDDIT_SUPERGRID_CONFIG, RedditLevel.CAMPAIGN);
	const { changeMutation, clearMutations, mutations } = useSupergridMutations<RedditSuperGridMutationsConfig>();
	const [touchedLevels, setTouchedLevels] = useState<RedditLevel[]>([]);

	const [api, setApi] = useState<GridApi | null>(null);

	const gridBodyElement = document.querySelector('#super-grid .ag-center-cols-viewport');
	const gridWidth = document.getElementById('super-grid')?.clientWidth ?? 0;
	const apolloClient = useApolloClient();

	const { t } = useTranslation();

	useEffect(() => {
		const handler = async (e: FillEndEvent) => {
			const initialStartIndex = e.initialRange.startRow?.rowIndex;
			const initialEndIndex = e.initialRange.endRow?.rowIndex;
			const finalStartIndex = e.finalRange.startRow?.rowIndex;
			const finalEndIndex = e.finalRange.endRow?.rowIndex;
			const nameColumn = e.finalRange.startColumn;

			if (nameColumn.getColDef().field !== 'name') {
				return;
			}

			if (
				initialStartIndex === undefined
                || initialEndIndex === undefined
                || finalStartIndex === undefined
                || finalEndIndex === undefined
			) {
				return;
			}

			const allRowIndexes = createSequence(finalStartIndex, finalEndIndex);

			const nonTargetRowIndexes = createSequence(initialStartIndex, initialEndIndex);

			const changedRowIndexes = allRowIndexes.filter(rowIndex => !nonTargetRowIndexes.includes(rowIndex));

			const changedRowNodes: IRowNode[] = [];

			e.api.forEachNode(node => {
				if (
					node.rowIndex
                    && changedRowIndexes.includes(node.rowIndex)
                    && isNamingFormula(node.data?.name)
				) {
					changedRowNodes.push(node);
				}
			});

			if (changedRowNodes.length === 0) {
				return;
			}

			handleCellLoading([nameColumn], changedRowNodes, true);

			let newNames: string[] | undefined;

			try {
				newNames = await transformNamingExpression(changedRowNodes.map((rowNode) => ({
					data:       rowNode.data,
					expression: rowNode.data.name,
				})), apolloClient);
			} catch (err: unknown) {
				console.error(err);

				newNames = Array(changedRowNodes.length).fill("Error: Couldn't generate name");
			}
			newNames?.forEach((newName, index) => {
				const rowNode = changedRowNodes[index];
				changeMutationWithAutoName(rowNode.data ?? {}, newName, rowNode.data?.name, true, changeMutation);
			});

			handleCellLoading([nameColumn], changedRowNodes, false);
		};
		api?.addEventListener('fillEnd', handler);

		return () => {
			api?.removeEventListener('fillEnd', handler);
		};
	}, [api]);

	const {
		updateDeepestVisibleLevel,
		deepestVisibleLevel,
		handleColumnVisibility,
		props: {
			context,
			columnDefs,
			onRowGroupOpened,
			onUndoStarted,
			onRedoStarted,
			onCellEditingStarted,
			onCellEditingStopped,
			onCellDoubleClicked,
			onCellKeyDown: onCellKeyDownSGProp,
			onBodyScroll,
			onCellValueChanged: onCellValueChangedProp,
		},
	} = useSuperGridEngine({
		gridBodyElement,
		gridApi:      api,
		gridTopLevel: RedditLevel.CAMPAIGN,
		config:	      REDDIT_SUPERGRID_CONFIG,
		plugins:      [
			columnEditorPlugin,
			useMutationsUndoRedoPlugin,
			useSuperGridPlugin(nameSizingPlugin, {
				lastNameColId: 'breakdownName',
			}),
			useSuperGridPlugin(validatePlugin, {}),
			elasticPropertyPlugin,
			useInstantEditPlugin,
		],
		hiddenLevels,
		visibleColumns,
		showAllElasticChildren: true,
	});

	useEffect(() => {
		preValidateRedditEntities({
			[RedditLevel.CAMPAIGN]: touchedLevels.includes(RedditLevel.CAMPAIGN) ? mutations[RedditLevel.CAMPAIGN] : [],
			[RedditLevel.GROUP]:    touchedLevels.includes(RedditLevel.GROUP) ? mutations[RedditLevel.GROUP] : [],
			[RedditLevel.AD]:      	touchedLevels.includes(RedditLevel.AD) ? mutations[RedditLevel.AD] : [],
		}).then(errors => context?.setValidations?.(errors));
	}, [mutations, touchedLevels]);

	const horizontalColDefs = useMemo(
		() => columnDefs?.filter(
			colDef => colDef.columnMode && colDef.columnMode !== ColumnModeTypeEnum.REGULAR,
		).map(colDef => ({ ...colDef, sortable: false, suppressMovable: true })),
		[columnDefs],
	);

	const gridOptions = useMemo(() => ({
		fillOperation: (params: FillOperationParams) => {
			if (params.column.getColDef().field === 'name') {
				const fillFirstRowIndex = params.api.getCellRanges()?.[0]?.startRow?.rowIndex;

				if (fillFirstRowIndex === undefined) {
					return params.values[0];
				}

				let namingExpression: string | undefined;

				params.api.forEachNode((node) => {
					if (node.rowIndex === fillFirstRowIndex) {
						namingExpression = node.data?.namingExpression;
					}
				});

				return namingExpression || params.values[0];
			}

			return params.values[0];
		},
	}), []);

	const CreativeHeadlineAIComponent = useSupergridAI({
		gridApi:     api,
		gridElement: gridBodyElement,
		columnName:  'creativeHeadline',
		placeholder: 'Enter Reddit post text...',
		getAiValues: getRedditOpenAiHeadlines,
	});

	const onGridReady = (e: GridReadyEvent) => {
		setApi(e.api);

		const firstColToBeFocused = e.columnApi?.getAllDisplayedColumns()?.[2]?.getColId();

		if (firstColToBeFocused) {
			focusSuperGridCell({ api: e.api, rowIndex: 0, field: firstColToBeFocused });
			e.api.startEditingCell({
				rowIndex: 0,
				colKey:   firstColToBeFocused,
			});
			e.api.setFocusedCell(0, firstColToBeFocused);
		}
	};

	const onHorizontalRowGroupOpened = (e: RowGroupOpenedEvent) => {
		onRowGroupOpened?.(e);
		e.api.refreshCells({ force: true });
	};

	const onRowDataUpdated = e => {
		handleColumnVisibility();
		updateDeepestVisibleLevel(e.api);
		refocusCell(e);
	};

	const onCellValueChanged = async (e: CellValueChangedEvent) => {
		setTouchedLevels(curr => [...curr, e.data?.level]);

		let newName: string | undefined;

		const isNameColumn = e.colDef.field === 'name';

		if (
			isNamingFormula(e.value)
            && isNameColumn
            && (e.source === 'edit' || e.source === 'paste')
		) {
			handleCellLoading([e.column], [e.node], true);

			try {
				const res = await transformNamingExpression([{
					data:       e.data,
					expression: e.value,
				}], apolloClient);
				[newName] = res;
			} catch (err) {
				console.error(err);
				newName = "Error: Couldn't generate name";
			}
		}

		changeMutationWithAutoName(e.data, newName, e.value, isNameColumn, changeMutation);
		handleCellLoading([e.column], [e.node], false);
		onCellValueChangedProp?.(e);

		const dependencies = REDDIT_CROSS_LEVEL_DEPENDENCIES[e.colDef?.colId ?? e.colDef?.field ?? '']?.(e.newValue);

		const affectedChildrenHashes = updateAndReturnAffectedChildren({
			parentHash: e.data?.hash,
			dependencies,
		}).map(child => child.hash);

		context?.unsetValidations?.(Object.keys(dependencies ?? {}), affectedChildrenHashes);
	};

	const hasValidations = context?.validations?.length > 0;

	const onCellKeyDown = (e: CellKeyDownEvent) => {
		onCellKeyDownSGProp?.(e);

		if ((e.event as KeyboardEvent).key === 'Escape') {
			refocusCell(e);
		}
	};

	const onScroll = useCallback((e: BodyScrollEvent) => {
		const columnEditor = document.getElementById('column-editor-wrapper');

		if (!columnEditor) {
			api?.stopEditing(true);
		}
		onBodyScroll?.(e);
	}, [onBodyScroll]);

	const isInvalidAdAccount = useMemo(
		() => isPendingAdAccount(
			Object.values(mutations)
				.flat()
				.map(mutation => getClientRecursively(mutation.parent))
				.find(client => client !== undefined),
		),
		[mutations],
	);

	const isInvalid = isInvalidAdAccount || hasValidations;

	return (
		<>
			{CreativeHeadlineAIComponent}

			<ValidationNavigator
				gridApi={api}
				mappedFields={MAPPED_COLUMN_NAMES}
			/>

			{
				deepestVisibleLevel !== 0
				&& <SuperGridAnchors
					key={deepestVisibleLevel}
					gridTopLevel={RedditLevel.CAMPAIGN}
					gridBodyElement={gridBodyElement}
					gridWidth={gridWidth}
					config={{
						...REDDIT_SUPERGRID_CONFIG,
						anchorComponents: {
							anchorBody: CreateAnchorBody,
						},
					}}
					gridApi={api}
					deepestVisibleLevel={deepestVisibleLevel}
					minWidth={36}
					leftBoundary="expand"
					pinLeft={true}
				/>
			}

			<div
				id="super-grid"
				className="ag-theme-alpine h-full w-full mb-[-11px] super-grid-horizontal"
			>
				<AgGridReact
					gridOptions={gridOptions}
					key="horizontal-mutator-grid"
					columnDefs={horizontalColDefs}
					context={{ ...context, isInCreation: true }}
					groupDisplayType="custom"
					treeData
					enableFillHandle
					onGridReady={onGridReady}
					rowSelection="multiple"
					suppressRowClickSelection
					headerHeight={32}
					undoRedoCellEditing
					onUndoStarted={onUndoStarted}
					onRedoStarted={onRedoStarted}
					rowHeight={32}
					suppressContextMenu
					enableRangeSelection
					onCellValueChanged={onCellValueChanged}
					onCellKeyDown={onCellKeyDown}
					defaultColDef={DEFAULT_REDDIT_COL_DEF}
					onCellDoubleClicked={onCellDoubleClicked}
					getDataPath={data => data.entityInheritance}
					suppressPropertyNamesCheck
					rowData={horizontalRowData}
					onBodyScroll={onScroll}
					suppressScrollOnNewData
					isGroupOpenByDefault={params => !params.rowNode?.key?.endsWith('_new')}
					onRowGroupOpened={onHorizontalRowGroupOpened}
					onRowDataUpdated={onRowDataUpdated}
					onCellEditingStarted={onCellEditingStarted}
					onCellEditingStopped={onCellEditingStopped}
					processCellFromClipboard={processValueFromJSON}
					processCellForClipboard={processValueToJSON}
					suppressDragLeaveHidesColumns
					getRowId={
						({ data }) => data?.id ?? data?.hash
                     ?? `${data.level}_${data.entityInheritance.join('_')}_create`
					}
				/>
			</div>

			<Floaty>
				<Button
					onClick={clearMutations}
					leftNode={
						<TrashCanIcon />
					}
					appearance={ButtonAppearance.OUTLINED}
					color={ButtonColor.RED}
					data-testid="discard-changes-button"
					size={ButtonSize.LARGE}
				/>

				<Tooltip
					placement="top"
					content={t('sc: supergrid creation floaty there are errors message', 'There are errors')}
					disabled={!hasValidations}
				>
					<Button
						title="Save draft"
						data-testid="save-draft-button"
						onClick={() => confirmMutations(api, 'PAUSED')}
						disabled={hasValidations}
						appearance={ButtonAppearance.OUTLINED}
						style={{ pointerEvents: hasValidations ? 'none' : undefined }}
						size={ButtonSize.LARGE}
					/>
				</Tooltip>

				<Tooltip
					placement="top"
					content={
						isInvalidAdAccount
							? t(
								'sc: supergrid creation floaty disabled while pending advertiser verification message',
								'Disabled while pending Advertiser verification',
							)
							: t('sc: supergrid creation floaty there are errors message', 'There are errors')
					}
					disabled={!isInvalid}
					delay={300}
					singleLine
				>
					<motion.div
						whileHover={isInvalid ? undefined : { x: 3 }}
					>
						<Button
							onClick={() => confirmMutations(api)}
							style={{ pointerEvents: isInvalid ? 'none' : undefined }}
							disabled={isInvalid}
							title="Publish to Reddit"
							data-testid="publish-button"
							size={ButtonSize.LARGE}
							rightNode={
								<DirectionRight
									width={16}
									height={16}
								/>
							}
						/>
					</motion.div>
				</Tooltip>
			</Floaty>
		</>
	);
};

export default RedditHorizontalMutatorGrid;
